import { useCallback } from 'react';
import { useAppSelector } from '../../app/hooks';
import { useFeatureVisibility } from '../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../utils/constants/visibilityKeys';
import { ACCESS_TIERS } from '../../store/investor/constants';
// USER_TYPE.COMPANY is 'ASSESSEE' here, unlike DealDrawerConstants' 'COMPANY'.
import { USER_TYPE } from '../../utils/constants/user';

const EXCLUDED_LEAD_ACCOUNT_TYPES = [USER_TYPE.LENDER, USER_TYPE.LENDER_TEAM, USER_TYPE.COMPANY];
const LEAD_ACCESS_TIERS = [ACCESS_TIERS.SCAN, ACCESS_TIERS.TRACK];

type LeadGateRow = {
    assesseeOrgId?: string;
    companyAccessType?: string;
};

/**
 * Shared "View All Properties" entry-point gate for the header icon, Leads row kebab and
 * Deals/Mandates row kebab. Restricted roles are not excluded: they get the drawer wherever
 * they see the row, and the table's own edit locks apply inside it.
 */
export const useViewAllPropertiesAccess = () => {
    const { viewAllPropertiesVisibility } = useFeatureVisibility({
        viewAllPropertiesVisibility: VISIBILITY_KEYS.VIEW_ALL_PROPERTIES.ENABLED,
    });
    const userData = useAppSelector((state) => state.user.userData);
    const accountType = userData?.accountType;
    // Row-independent half of the lead gate: kill switch + account type.
    const isLeadPropertiesEnabled = !!viewAllPropertiesVisibility.show && !EXCLUDED_LEAD_ACCOUNT_TYPES.includes(accountType);

    const canViewLeadProperties = useCallback(
        (row: LeadGateRow | null | undefined, routeOrgId?: string): boolean => {
            if (!isLeadPropertiesEnabled) return false;
            // Positive list: the pre-fetch stub (companyAccessType '') fails closed here.
            if (!LEAD_ACCESS_TIERS.includes(row?.companyAccessType as any)) return false;
            // Company page only: stay hidden until the fetched company matches the route.
            if (routeOrgId !== undefined && row?.assesseeOrgId !== routeOrgId) return false;
            return true;
        },
        [isLeadPropertiesEnabled],
    );

    const canViewDealProperties = viewAllPropertiesVisibility.show && accountType !== USER_TYPE.COMPANY;

    return { isLeadPropertiesEnabled, canViewLeadProperties, canViewDealProperties };
};

export default useViewAllPropertiesAccess;
